/**
 * Read block wrapping from schema placeholders and apply it to transactions.
 */

import { Node as EditorNode, NodeType, ResolvedPos } from 'prosemirror-model'
import { TextSelection, Transaction } from 'prosemirror-state'

/**
 * The [open, close] delimiters for a block type's `placeholder`, or [] if it has none.
 */
export function getWrapChars(blockType: NodeType) {
  const { placeholder } = blockType.spec
  return placeholder ? [placeholder.prefix, placeholder.suffix] : []
}

export function isWrappedBlock(blockType: NodeType) {
  return getWrapChars(blockType).length > 0
}

/**
 * True when the block has no content, or only its default wrapping.
 */
export function isEmptyBlock(block: EditorNode) {
  if (block.content.size === 0) return true

  const { placeholder } = block.type.spec
  if (!placeholder) return false

  return block.textContent === placeholder.prefix + placeholder.suffix
}

/**
 * Return the schema suffix remaining at a logical block end, or an empty string
 * if only whitespace remains. Return undefined when other content follows or
 * the position is outside a textblock.
 */
export function getLogicalEndSuffix($pos: ResolvedPos): string | undefined {
  const block = $pos.parent
  if (!block.isTextblock) return

  const trailing = block
    .textBetween($pos.parentOffset, block.content.size)
    .trimEnd()
  if (trailing === '') return ''
  if (trailing === block.type.spec.placeholder?.suffix) return trailing
}

function parseSingleBlockSelection(tr: Transaction) {
  const { $from } = tr.selection
  const start = $from.start()
  const end = $from.end()
  const blockType = $from.parent.type

  return { $from, start, end, blockType }
}

function calcWhitespace($from: ResolvedPos) {
  const { textContent } = $from.parent
  return textContent.length - textContent.trimEnd().length
}

/**
 * True when the block's text already begins and ends with its full
 * placeholder, which may be more than one character long.
 */
export function checkWrap(tr: Transaction) {
  const { $from, blockType } = parseSingleBlockSelection(tr)

  const [prefix, suffix] = getWrapChars(blockType)
  if (prefix === undefined) return false

  const { textContent } = $from.parent
  return textContent.startsWith(prefix) && textContent.endsWith(suffix)
}

export function maybeTrimWhitespace(tr: Transaction) {
  const { $from, end } = parseSingleBlockSelection(tr)
  const ws = calcWhitespace($from)
  if (ws > 0) tr.deleteRange(end - ws, end)
  return tr
}

/*
utility function to help figure out if/when/how much to massage
the existing selection after injecting new wrapping characters

i feel like this is overly convoluted, but i wasted more time than
i should have trying to simplify with no joy.
*/
function wrapSelectionAdjustment(tr: Transaction, wasWrapped: boolean) {
  const { start, end, blockType } = parseSingleBlockSelection(tr)
  const [prefix, suffix] = getWrapChars(blockType)
  const from = tr.selection.$from.pos
  const to = tr.selection.$to.pos
  const fromAtStart = from === start
  const toAtEnd = to === end
  let fromAdj = 0
  let toAdj = 0

  // previously wrapped blocks require a fair amount of adjustment
  if (wasWrapped) {
    // dont modify plain cursors positioned at the beginning of a block
    const shouldAdjustTo = !(fromAtStart && tr.selection.empty)

    // if the from pos is interior, shift the righthand side of the selection
    // past the whole opening delimiter
    if (!fromAtStart) fromAdj += prefix.length
    if (shouldAdjustTo) toAdj += suffix.length
    // empty blocks are another special case
    if (tr.selection.$from.parent.nodeSize === 2) {
      toAdj -= suffix.length
    }
  } else if (toAtEnd) {
    // if the block wasnt wrapped shift only the righthand
    // side of selections that touch the end of the block
    toAdj -= suffix.length
  }

  // if we started with a plain cursor and the modifications above
  // resulted in drift between the to and from, the 'to' is canonical
  if (tr.selection.empty) fromAdj = toAdj

  return { from, to, fromAdj, toAdj }
}

export function maybeStripWrapChars(tr: Transaction) {
  const { start, end, blockType } = parseSingleBlockSelection(tr)
  const [prefix, suffix] = getWrapChars(blockType)
  const wasWrapped = checkWrap(tr)

  // only delete wrapping characters when they are present
  if (wasWrapped) {
    tr.deleteRange(tr.mapping.map(end - suffix.length), tr.mapping.map(end))
    tr.deleteRange(tr.mapping.map(start), tr.mapping.map(start + prefix.length))
  }

  return tr
}

export function maybeAddWrapChars(tr: Transaction, wasWrapped: boolean) {
  const { start, end, blockType } = parseSingleBlockSelection(tr)
  const [prefix, suffix] = getWrapChars(blockType)
  const isWrapped = checkWrap(tr)
  const shouldWrap = !isWrapped && prefix !== undefined

  if (shouldWrap) {
    // calculate adjustments *before* the transaction is mutated
    const { from, to, fromAdj, toAdj } = wrapSelectionAdjustment(tr, wasWrapped)

    // we avoid mapping positions here purposefully
    // we want to add characters where the start
    // and end are *now*, not where they *used to* be
    // because of this, its important to insert the last wrapping
    // character before the first one.
    tr.insertText(suffix, end)
    tr.insertText(prefix, start)

    // preserve the original selection
    tr.setSelection(
      TextSelection.create(
        tr.doc,
        tr.mapping.map(from) + fromAdj,
        tr.mapping.map(to) + toAdj,
      ),
    )
  }

  return tr
}
